<p-toast />
<p-confirmDialog />
<app-shipment-commercial-party-correction [shipment]="shipment"
    (corrected)="onBillToPartyCorrected()"></app-shipment-commercial-party-correction>
<app-page-shell>
    @if (loading) {
        <div class="loading-state">
            <p-progressSpinner strokeWidth="4"></p-progressSpinner>
            <span>Loading shipment…</span>
        </div>
    } @else if (error || !shipment) {
        <div class="empty-state">
            <i class="pi pi-exclamation-circle"></i>
            <strong>{{ error || 'Shipment not found.' }}</strong>
            <p-button label="Back to shipments" [routerLink]="['/shipment']" severity="secondary"></p-button>
        </div>
    } @else {
        <app-page-hero
            eyebrow="SIMPLE OPERATIONS"
            [title]="shipment.shipmentNumber || ('Shipment #' + shipment.shipmentId)"
            [subtitle]="shipment.externalReference ? 'Customer reference: ' + shipment.externalReference : 'Operational shipment view'">
            <div actions>
                <p-tag [value]="statusLabel(shipment.shipmentStatus)" severity="success"></p-tag>
                @if (canPostToAccounts()) {
                    <p-button
                        [label]="isPostedToAccounts() ? 'Update Accounts' : ('pages.shipmentDetail.actions.postToAccounts' | transloco)"
                        icon="fa-solid fa-file-invoice-dollar"
                        severity="success"
                        [loading]="postingToAccounts"
                        [disabled]="postingToAccounts"
                        (onClick)="postToAccounts()">
                    </p-button>
                }
                @if (canCorrectBillToParty()) {
                    <p-button label="Correct Bill-to Party" icon="pi pi-pencil" severity="secondary"
                        [outlined]="true" (onClick)="openBillToPartyCorrection()"></p-button>
                }
                <p-button
                    label="Switch to advanced mode"
                    icon="pi pi-sliders-h"
                    severity="secondary"
                    [outlined]="true"
                    (onClick)="switchToAdvanced()">
                </p-button>
            </div>
        </app-page-hero>

        <main class="operations-grid">
            <div class="main-column">
                <section class="route-card">
                    <div class="location">
                        <span>PICKUP</span>
                        <strong>{{ shipment.sourceLocation?.name || 'Pickup pending' }}</strong>
                        <small>{{ shipment.sourceLocation?.shortDescription || shipment.pickupCity }}</small>
                    </div>
                    <div class="route-line">
                        <span></span>
                        <i class="fa-solid fa-truck"></i>
                        <span></span>
                    </div>
                    <div class="location destination">
                        <span>DROP-OFF</span>
                        <strong>{{ shipment.destinationLocation?.name || 'Drop-off pending' }}</strong>
                        <small>{{ shipment.destinationLocation?.shortDescription || shipment.dropoffCity }}</small>
                    </div>
                </section>

                <section class="summary-strip">
                    <div>
                        <span>Pickup</span>
                        <strong>{{ shipment.requestedBookingTime | date:'medium' }}</strong>
                    </div>
                    <div>
                        <span>Customer</span>
                        <strong>{{ shipment.company?.name || shipment.customer?.name || '—' }}</strong>
                    </div>
                    <div>
                        <span>Load</span>
                        <strong>{{ cargoQuantity(selectedDelivery) }}</strong>
                    </div>
                    <div>
                        <span>Deliveries</span>
                        <strong>{{ shipment.deliveries?.length || 0 }}</strong>
                    </div>
                </section>

                @if ((shipment.deliveries?.length || 0) > 1) {
                    <section class="delivery-list-section">
                        <div class="card-heading">
                            <div>
                                <span>DELIVERIES</span>
                                <h2>Select a delivery</h2>
                            </div>
                            <small>{{ shipment.deliveries.length }} assignments</small>
                        </div>
                        <div class="delivery-cards">
                            @for (delivery of shipment.deliveries; track delivery.deliveryId) {
                                <button
                                    type="button"
                                    class="delivery-card"
                                    [class.active]="selectedDeliveryId === delivery.deliveryId"
                                    (click)="selectDelivery(delivery)">
                                    <span>{{ delivery.deliveryNumber }}</span>
                                    <strong>{{ driverName(delivery) }}</strong>
                                    <small>{{ statusLabel(delivery.jobStatus) }}</small>
                                </button>
                            }
                        </div>
                    </section>
                }

                <section class="execution-card">
                    <div class="card-heading">
                        <div>
                            <span>ASSIGNMENT</span>
                            <h2>{{ selectedDelivery?.deliveryNumber || 'Assignment required' }}</h2>
                        </div>
                        <p-tag
                            [value]="statusLabel(selectedDelivery?.jobStatus)"
                            [severity]="selectedDelivery ? 'info' : 'warn'">
                        </p-tag>
                    </div>

                    <div class="crew-grid">
                        <div class="crew-item">
                            <div class="crew-icon"><i class="fa-solid fa-user"></i></div>
                            <div>
                                <span>Driver</span>
                                <strong>{{ driverName(selectedDelivery) }}</strong>
                                <small>{{ selectedDelivery?.deliveryDriver?.phoneNumber }}</small>
                            </div>
                        </div>
                        <div class="crew-item">
                            <div class="crew-icon"><i class="fa-solid fa-truck"></i></div>
                            <div>
                                <span>Vehicle</span>
                                <strong>{{ vehicleLabel(selectedDelivery) }}</strong>
                                <small>{{ selectedDelivery?.deliveryVehicle?.type?.name }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="progress-block">
                        <div>
                            <span>Delivery progress</span>
                            <strong>{{ progressPercent(selectedDelivery) }}%</strong>
                        </div>
                        <p-progressBar [value]="progressPercent(selectedDelivery)" [showValue]="false"></p-progressBar>
                        <small>{{ progressCaption(selectedDelivery) }}</small>
                    </div>
                    <div class="operations-summary">
                        <div>
                            <span>Latest operational phase</span>
                            <strong>{{ statusLabel(operationStatus?.currentPhase || operationStatus?.deliveryStatus) }}</strong>
                        </div>
                        <div [class.has-exception]="exceptionEvents.length">
                            <span>Exceptions</span>
                            <strong>{{ exceptionEvents.length ? exceptionEvents.length + ' require attention' : 'None reported' }}</strong>
                        </div>
                    </div>
                    @if (selectedDelivery && permits('REASSIGN')) {
                        <p-button
                            label="Reassign driver or vehicle"
                            icon="pi pi-user-edit"
                            severity="secondary"
                            [outlined]="true"
                            [routerLink]="['/shipment/delivery', shipment.shipmentId, 'generate']">
                        </p-button>
                    }
                </section>

                <div class="activity-load-grid">
                <section class="timeline-card">
                    <div class="card-heading">
                        <div><span>DELIVERY TIMELINE</span><h2>Latest activity</h2></div>
                        @if (timelineLoading) { <i class="pi pi-spin pi-spinner"></i> }
                    </div>
                    <div class="simple-timeline">
                        @for (event of timelineEvents; track event.eventId || $index) {
                            <div class="timeline-event" [class.exception]="event.overridden || (event.gateResult && event.gateResult !== 'PASSED')">
                                <i class="pi pi-circle-fill"></i>
                                <div>
                                    <strong>{{ eventLabel(event) }}</strong>
                                    <span>{{ event.description || statusLabel(event.previousState) + ' → ' + statusLabel(event.nextState) }}</span>
                                    <small>{{ event.eventAt | date:'medium' }}</small>
                                </div>
                            </div>
                        }
                        @if (!timelineLoading && !timelineEvents.length) {
                            <div class="inline-empty">No workflow activity recorded yet.</div>
                        }
                    </div>
                </section>

                <section class="cargo-card">
                    <div class="card-heading">
                        <div>
                            <span>LOAD</span>
                            <h2>Cargo summary</h2>
                        </div>
                    </div>
                    <div class="cargo-lines">
                        @for (line of (selectedDelivery?.payloadItemList?.length ? selectedDelivery?.payloadItemList : shipment.payloadItemList); track $index) {
                            <div class="cargo-line">
                                <div>
                                    <strong>{{ line.itemType?.name || line.itemDescription || line.itemDesciption || 'Cargo' }}</strong>
                                    <small>{{ line.itemDescription || line.itemDesciption }}</small>
                                </div>
                                <span>{{ line.itemQuantity?.quantity || '—' }} {{ line.itemQuantity?.uom?.shortName }}</span>
                                <span>{{ payloadTotalWeightLabel(line) }}</span>
                            </div>
                        } @empty {
                            <div class="inline-empty">No cargo lines recorded.</div>
                        }
                    </div>
                </section>

                </div>

                @if (show('notes') && shipment.remark?.remarksHtml) {
                    <section class="notes-card">
                        <div class="card-heading">
                            <div><span>NOTES</span><h2>Operational instructions</h2></div>
                        </div>
                        <p>{{ shipment.remark?.remarksHtml }}</p>
                    </section>
                }
            </div>

            <aside class="side-column">
                <section class="next-action-card">
                    <span>NEXT ACTION</span>
                    <strong>{{ nextActionLabel() }}</strong>
                    <p>{{ selectedDelivery ? statusLabel(selectedDelivery.jobStatus) : 'Choose a driver and vehicle to start execution.' }}</p>
                    @if (canPostToAccounts()) {
                        <p-button
                            [label]="nextActionLabel()"
                            icon="fa-solid fa-file-invoice-dollar"
                            iconPos="right"
                            severity="success"
                            [loading]="postingToAccounts"
                            [disabled]="postingToAccounts"
                            (onClick)="postToAccounts()"
                            styleClass="w-full">
                        </p-button>
                    } @else if (isPostedToAccounts()) {
                        <p-button
                            [label]="nextActionLabel()"
                            icon="fa-solid fa-arrow-up-right-from-square"
                            iconPos="right"
                            (onClick)="openFinancials()"
                            styleClass="w-full">
                        </p-button>
                    } @else {
                        <p-button
                            [label]="nextActionLabel()"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            [routerLink]="nextActionRoute()"
                            styleClass="w-full">
                        </p-button>
                    }
                </section>

                @if (accountsPostingResult) {
                    <section class="pod-card">
                        <div class="card-heading">
                            <div>
                                <span>{{ 'pages.shipmentDetail.actions.accountsStaging' | transloco }}</span>
                                <h2>{{ 'pages.shipmentDetail.actions.postToAccountsSuccess' | transloco }}</h2>
                            </div>
                        </div>
                        <div class="pod-row">
                            <i class="pi pi-check-circle success"></i>
                            <div>
                                <strong>{{ accountsPostingResult.bulkCostIds.length }} / {{ accountsPostingResult.receivableIds.length }} / {{ accountsPostingResult.payableIds.length }}</strong>
                                <small>{{ 'pages.shipmentDetail.actions.documentCountOrder' | transloco }}</small>
                            </div>
                        </div>
                        @if (accountsPostingResult.channelOutcomes['SISTER_COST_RECONCILIATION'] === 'REQUIRED_POSTED_BULK_COST') {
                            <div class="pod-row">
                                <i class="pi pi-exclamation-triangle"></i>
                                <small>A truck cost was already posted in the shipment company. Finance must reconcile it before posting cost or a payable in the truck-owning company.</small>
                            </div>
                        }
                        @if (accountsPostingResult.channelOutcomes['FINANCE_RECONCILIATION']) {
                            <p class="text-orange-600"><i class="pi pi-exclamation-triangle"></i>
                                {{ accountsPostingResult.channelOutcomes['FINANCE_RECONCILIATION'] }}</p>
                        }
                        @if (accountsPostingResult.channelOutcomes['TRUCK_OWNER_RECONCILIATION'] === 'LEGACY_SHIPMENT_COMPANY_DOCUMENT'
                            && accountsPostingResult.channelOutcomes['SISTER_COST_RECONCILIATION'] !== 'REQUIRED_POSTED_BULK_COST') {
                            <div class="pod-row">
                                <i class="pi pi-exclamation-triangle"></i>
                                <small>A delivery cost or payable still belongs to the shipment company. Finance must reconcile it before posting in the truck-owning company.</small>
                            </div>
                        }
                        @if (accountsPostingResult.bulkCostIds.length) {
                            <p-button [label]="'pages.shipmentDetail.actions.bulkCostDocuments' | transloco:{ count: accountsPostingResult.bulkCostIds.length }"
                                icon="fa-solid fa-receipt" severity="secondary" [outlined]="true"
                                [routerLink]="['/accounts/payables/bulk-costs']" styleClass="w-full"></p-button>
                        }
                        @if (accountsPostingResult.receivableIds.length) {
                            <p-button [label]="'pages.shipmentDetail.actions.receivableDocuments' | transloco:{ count: accountsPostingResult.receivableIds.length }"
                                icon="fa-solid fa-file-invoice-dollar" severity="secondary" [outlined]="true"
                                [routerLink]="['/accounts/receivables/to-be-invoiced']" styleClass="w-full"></p-button>
                        }
                        @if (accountsPostingResult.payableIds.length) {
                            <p-button [label]="'pages.shipmentDetail.actions.payableDocuments' | transloco:{ count: accountsPostingResult.payableIds.length }"
                                icon="fa-solid fa-file-invoice" severity="secondary" [outlined]="true"
                                [routerLink]="['/accounts/payables/to-be-billed']" styleClass="w-full"></p-button>
                        }
                    </section>
                }

                <section class="pod-card">
                    <div class="card-heading">
                        <div><span>PROOF OF DELIVERY</span><h2>POD readiness</h2></div>
                    </div>
                    <div class="pod-row">
                        <i [class]="selectedDelivery?.deliveryActionData?.recipientSignatureUrl ? 'pi pi-check-circle success' : 'pi pi-clock'"></i>
                        <div>
                            <strong>{{ selectedDelivery?.deliveryActionData?.recipientSignatureUrl ? 'Signature captured' : 'Awaiting signature' }}</strong>
                            <small>{{ selectedDelivery?.deliveryActionData?.receiverName || 'Receiver not confirmed' }}</small>
                        </div>
                    </div>
                </section>

                @if (show('attachments')) {
                    <section class="files-card">
                        <div class="card-heading">
                            <div><span>FILES</span><h2>Documents</h2></div>
                            <small>{{ (shipment.filesList?.length || 0) + (selectedDelivery?.filesList?.length || 0) }}</small>
                        </div>
                        <div class="file-list">
                            @for (file of shipment.filesList; track file.attachmentId) {
                                <a [href]="file.fileUrl" target="_blank" rel="noopener">
                                    <i class="pi pi-file"></i><span>{{ file.fileName }}</span>
                                </a>
                            }
                            @for (file of selectedDelivery?.filesList || []; track file.attachmentId) {
                                <a [href]="file.fileUrl" target="_blank" rel="noopener">
                                    <i class="pi pi-file"></i><span>{{ file.fileName }}</span>
                                </a>
                            }
                            @if (!(shipment.filesList?.length || selectedDelivery?.filesList?.length)) {
                                <div class="inline-empty">No documents attached.</div>
                            }
                        </div>
                    </section>
                }
            </aside>
        </main>
    }
</app-page-shell>
<app-shipment-accounts-review-dialog [(visible)]="accountsReviewVisible" [shipmentId]="shipment?.shipmentId" [shipmentDetail]="shipment" [posting]="postingToAccounts" (confirmed)="confirmAccountsPost()"></app-shipment-accounts-review-dialog>
